// Theme + data accessors for the closing calendars.
//
// The calendars plot the CARDS from `GET /closing/schedule` (closing-readiness.md
// §2): every loan whose funding date falls in the visible window, across both
// pipelines — preclose closings AND acquisition trade settlements — each with
// its deterministic readiness level. The old client-side "Clear To Close" slice
// of the pipeline list is gone; the card feed is the projection.
//
// Card accessors live in components/closingReadiness/closingReadinessUtils.js
// (shared with the drilldown Closing tab); this file re-exports the ones the
// calendar views use and keeps the calendar-local palette.

import { format } from "date-fns";

import {
  C,
  MONO,
  READINESS,
  READINESS_ORDER,
  bucketCardsByDay,
  cardAddress,
  cardAmount,
  cardAmountCompact,
  cardAmountLabel,
  cardChipLabel,
  cardDate,
  cardDisplayName,
  cardId,
  cardLevel,
  cardLevelMeta,
  dayKey,
  formatCompactDollar,
  isSoftDate,
  parseLocalDate,
  prettyDate,
  sumCardAmounts,
} from "../../components/closingReadiness/closingReadinessUtils";

// ── Palette ──────────────────────────────────────────────────────────────────
// Lives in closingReadinessUtils now (the drawer + Closing tab sections read
// it); re-exported under the names the calendar views were written against.
export { C, MONO };

// ── Status = readiness level ─────────────────────────────────────────────────
// The filter chips + pills key off `readinessLevel`. `STATUS` keeps the shape
// the views were written against ({ key, label, c, bg }).
export const STATUS = READINESS;
export const STATUS_ORDER = READINESS_ORDER;

export const deriveStatus = cardLevel;
export const statusMetaForLoan = cardLevelMeta;

// ── Card accessors (names kept from the loan-era API) ───────────────────────
export const loanId = cardId;
export const loanDisplayName = cardDisplayName;
export const loanChipLabel = cardChipLabel;
export const loanPropertyAddress = cardAddress;
export const loanAmount = cardAmount;
export const loanAmountLabel = cardAmountLabel;
export const loanAmountCompact = cardAmountCompact;
export const sumLoanAmounts = sumCardAmounts;
export { formatCompactDollar, dayKey, prettyDate, isSoftDate };

// ── Dates ────────────────────────────────────────────────────────────────────
export const parseClosingDate = parseLocalDate;

// Effective calendar date for a card: closedDate for closed loans, otherwise
// the funding date (scheduled → settlement → expected per `dateBasis`).
export const loanClosingDate = cardDate;

// A firm date (scheduled / settlement / funded) vs a soft expected placement.
export const isScheduledClosing = (card) => !isSoftDate(card) && card?.dateBasis !== "none";

export const bucketLoansByDay = bucketCardsByDay;

// yyyy-MM-dd for the feed's `start` / `end` params.
export const ymd = (date) => format(date, "yyyy-MM-dd");

// The order the detail drawer pages through cards in (‹ 6 / 8 ›): by calendar
// day, then loan number; dateless cards last.
export const orderCards = (cards) =>
  [...(Array.isArray(cards) ? cards : [])].sort((a, b) => {
    const da = cardDate(a)?.getTime() ?? Infinity;
    const db = cardDate(b)?.getTime() ?? Infinity;
    if (da !== db) return da - db;
    return (a?.loanNumber ?? 0) - (b?.loanNumber ?? 0);
  });
